/**
 * compute.ts — the in-browser deterministic engine (rewritten for the typed-
 * connection + buffer/source/sink + signal-edge model).
 *
 * Each tick, every node produces an `outflow` from its `inflow` + `stock` +
 * gate function; edges carry the flow (split by weights, or per-connection for
 * typed edges). Money edges derive from other edges or fixed rates. Signal
 * edges carry read-only state into a target's formula scope — they are part
 * of the evaluation dependency graph but never move stock.
 *
 * Two passes:
 *   1. Node pass in topological order over physical + signal edges. A node's
 *      inflow = Σ incoming physical edge flows; its formula scope includes
 *      every signal edge's exposed state. Apply the gate, fan outflow across
 *      out-edges.
 *   2. Money pass. For each money edge, resolve its connections (derive →
 *      source edge flow × expr; rate → fixed). Accrue to money-group nodes.
 *
 * Cycles: if the physical+signal subgraph has a cycle, that's a real modelling
 * error (the user wired a feedback loop that can't resolve) — throw CycleError
 * naming the cycle. The old "fall back to authored order" hack is gone: we
 * surface the error so the UI can show the user what's wrong. Money derivation
 * cycles are still broken by construction (money is resolved after physical
 * flow, and a derive-connection references an already-computed edge).
 *
 * Domain-free: operates only on Graph + params + state. Same purity test as
 * the rest of engine/ — no food/kitchen/delivery references.
 */
import type {
  EdgeConnection,
  EdgeId,
  GateFn,
  Graph,
  GraphEdge,
  GraphNode,
  NodeId,
  ParamSpec,
  ScenarioBundle,
  TickPayload,
} from './types';
import { evalExpr, ExprError } from './expr/walker';

/** Resolved param values for a node or edge — flat map of key → number. */
export type Params = Record<string, number>;

export interface ResolvedParams {
  nodes: Record<NodeId, Params>;
  edges: Record<EdgeId, Params>;
}

/** Initialise from a bundle: every ParamSpec's `value` becomes the live value. */
export function resolveInitialParams(bundle: ScenarioBundle): ResolvedParams {
  const nodes: Record<NodeId, Params> = {};
  const edges: Record<EdgeId, Params> = {};
  for (const n of bundle.graph.nodes) {
    const p: Params = {};
    for (const spec of n.params) p[spec.key] = spec.value;
    for (const spec of n.gate?.params ?? []) p[spec.key] = spec.value;
    for (const spec of n.source?.distParams ?? []) p[spec.key] = spec.value;
    nodes[n.id] = p;
  }
  for (const e of bundle.graph.edges) {
    const p: Params = {};
    // Edge weight joins the param system under the implicit key 'weight'
    // (addressable as e:<edgeId>.weight). An explicit edge param with the same
    // key wins — it was authored deliberately.
    p.weight = e.weight;
    for (const spec of e.params) p[spec.key] = spec.value;
    edges[e.id] = p;
  }
  return { nodes, edges };
}

/** Thrown when the physical+signal subgraph has a cycle. UI surfaces it. */
export class CycleError extends Error {
  readonly cycle: NodeId[];
  constructor(cycle: NodeId[]) {
    super(`feedback cycle detected: ${cycle.join(' → ')}`);
    this.name = 'CycleError';
    this.cycle = cycle;
  }
}

const PHYSICAL_KINDS = new Set(['goods', 'orders', 'customers', 'custom']);

/** True when an edge carries conserved resource INTO its target. A
 *  `conserves: false` edge is excluded on the inflow side (target inflow,
 *  assembler accrual, topo dependencies) but still receives fan-out flow for
 *  display — the flow "evaporates" at the target instead of stocking there. */
const isPhysicalIn = (e: GraphEdge): boolean => PHYSICAL_KINDS.has(e.flowKind) && e.conserves !== false;

/** Compute one tick. Throws CycleError on unsolvable feedback, ExprError on a
 *  bad formula (caught upstream and surfaced in the UI).
 *
 *  `prevState` (optional) carries the previous tick's per-node state map so
 *  accumulators like a sink's `consumed` or a buffer's running overflow total
 *  persist across ticks. Without it, state is recomputed from scratch each
 *  call (fine for a one-shot, wrong for a running sim — the adapter threads
 *  this through). */
export function computeTick(
  graph: Graph,
  params: ResolvedParams,
  stocks: Record<NodeId, number>,
  step: number,
  prevState?: Record<NodeId, Record<string, number>>,
): TickPayload {
  const nodeById = new Map(graph.nodes.map((n) => [n.id, n]));
  const inEdges = new Map<NodeId, GraphEdge[]>();
  const outEdges = new Map<NodeId, GraphEdge[]>();
  for (const n of graph.nodes) {
    inEdges.set(n.id, []);
    outEdges.set(n.id, []);
  }
  for (const e of graph.edges) {
    inEdges.get(e.target)?.push(e);
    outEdges.get(e.source)?.push(e);
  }

  // Migrate legacy `derive` blocks → connections at compute time so old
  // scenarios keep working until they're re-authored. The legacy `priceParam`
  // referenced an edge param (the price lives on the same edge as the derive),
  // so the migrated expr reads `params.<price>` (edge params), NOT source.*.
  const effectiveConnections = (e: GraphEdge): EdgeConnection[] => {
    if (e.connections && e.connections.length > 0) return e.connections;
    if (e.derive?.fromEdge && e.derive.priceParam) {
      return [{ kind: 'derive', expr: `x * params.${e.derive.priceParam}`, sourceParam: e.derive.fromEdge }];
    }
    if (e.derive?.rateParam) {
      return [{ kind: 'rate', expr: 'x', sourceParam: e.derive.rateParam }];
    }
    return [];
  };

  // ─── Topo order over physical edges only ──────────────────────────────────
  // Signal edges are NOT dependencies: they expose last-tick-lagged state into
  // a target's formula scope. This is the standard way sim engines break the
  // algebraic cycle of a feedback loop (delivery backs up → kitchen throttles
  // → delivery drains → kitchen resumes) — the signal carries a one-tick-old
  // value, so the resource flow stays acyclic and the loop resolves over time.
  // Money is also resolved in a second pass.
  const order = topoOrder(graph, isPhysicalIn);

  // Per-edge physical flow (the displayed + conserved number).
  const edgeFlow: Record<EdgeId, number> = {};
  // Per-node outflow (headful quantity, used for fan-out + display).
  const nodeOutflow: Record<NodeId, number> = {};
  // Per-node state accumulator (for sink.consumed, buffer.overflow, custom).
  // Seed from prevState so accumulators persist across ticks (the adapter
  // threads the previous tick's state in).
  const nodeState: Record<NodeId, Record<string, number>> = {};
  if (prevState) {
    for (const [id, st] of Object.entries(prevState)) {
      nodeState[id] = { ...st };
    }
  }
  // Signal exposures delivered to each target via signal edges: target → {alias: value}.
  const signalsDelivered: Record<NodeId, Record<string, number>> = {};

  // First: pre-compute signal deliveries (signal edges expose source state to
  // target). The source state must be available — which means the source node
  // must be evaluated before the target. That ordering is enforced by the topo
  // sort treating signal edges as dependencies. We resolve signal values
  // lazily during the node pass (after the source has been evaluated).
  const signalEdgesInto = new Map<NodeId, GraphEdge[]>();
  for (const n of graph.nodes) signalEdgesInto.set(n.id, []);
  for (const e of graph.edges) {
    if (e.flowKind === 'signal') signalEdgesInto.get(e.target)?.push(e);
  }

  for (const id of order) {
    const node = nodeById.get(id);
    if (!node) continue;

    // Resolve incoming signal exposures now (sources already evaluated).
    const signals: Record<string, number> = {};
    for (const sig of signalEdgesInto.get(id) ?? []) {
      const conn = effectiveConnections(sig)[0];
      const sourceNode = sig.source;
      const srcStateKey = conn?.sourceParam ?? 'stock';
      const sourceVal = readNodeState(sourceNode, srcStateKey, stocks, nodeState);
      const transformed = applyConnectionExpr(conn, sourceVal, params.edges[sig.id] ?? {}, nodeById.get(sourceNode), node, params);
      // The signal is exposed under the edge's id (so formulas can reference
      // signals.<sourceId> or just use nodes.<sourceId>.<key> directly).
      signals[`signal_${sourceNode}`] = transformed;
    }
    if (Object.keys(signals).length > 0) signalsDelivered[id] = signals;

    // ─── Inflow (physical edges only) ──────────────────────────────────────
    const physicalIn = (inEdges.get(id) ?? []).filter(isPhysicalIn);
    let inflow = physicalIn.reduce((sum, e) => sum + (edgeFlow[e.id] ?? 0), 0);

    // Boundary source emits its rate (replaces physical inflow for
    // conserves=false sources; adds to inflow otherwise).
    const isBoundarySource = node.kind === 'source' && node.conserves === false;
    const rate = sourceRate(node, params.nodes[id] ?? {}, step);
    if (isBoundarySource) {
      inflow = rate;
    } else if (node.kind === 'source' || node.source) {
      inflow += rate;
    }

    const stock = stocks[id] ?? 0;
    const p = params.nodes[id] ?? {};
    const available = stock + inflow;

    // ─── Apply node-kind semantics ─────────────────────────────────────────
    let outflow: number;
    const scope = buildNodeScope(stock, inflow, p, graph, params, edgeFlow, stocks, nodeState);
    switch (node.kind) {
      case 'sink':
        outflow = 0; // sinks consume, never push downstream
        // consumed is a running total (cumulative fulfilled orders / churned
        // customers); it accumulates across ticks via the prevState seed.
        nodeState[id] = { ...(nodeState[id] ?? {}), consumed: (nodeState[id]?.consumed ?? 0) + inflow };
        break;
      case 'buffer': {
        // A buffer has TWO caps:
        //   - maxStock (default `capacity`): the ceiling on accumulated stock.
        //     Above this, excess spills to state.overflow and is held aside.
        //   - the gate's `capacity`: the per-tick drain rate (what the gate
        //     releases downstream). For a threshold gate, this is the
        //     throughput; for a pass gate, infinite.
        const maxStock = p.maxStock ?? p.capacity ?? Number.POSITIVE_INFINITY;
        const overflow = Math.max(0, available - maxStock);
        if (overflow > 0) nodeState[id] = { ...(nodeState[id] ?? {}), overflow };
        else if (nodeState[id]?.overflow !== undefined) {
          nodeState[id] = { ...nodeState[id], overflow: 0 };
        }
        // The drainable amount is what's within the ceiling; the gate decides
        // how much of THAT to release (e.g. threshold with capacity=5 ships 5).
        const drainable = Math.min(available, maxStock);
        outflow = applyGate(node, drainable, inflow, p, scope);
        break;
      }
      case 'gate':
        outflow = applyGate(node, available, inflow, p, scope);
        break;
      case 'delay': {
        const ticksKey = node.delay?.ticksParam ?? 'ticks';
        const delayTicks = Math.max(0, Math.floor(p[ticksKey] ?? 1));
        const curState = { ...(nodeState[id] ?? {}) };
        
        if (delayTicks === 0) {
          let drainable = available;
          for (const k of Object.keys(curState)) {
            if (k.startsWith('transit_')) delete curState[k];
          }
          curState.inTransit = 0;
          outflow = applyGate(node, drainable, inflow, p, scope);
        } else {
          let drainable = curState['transit_0'] ?? 0;
          for (let i = 0; i < delayTicks - 1; i++) {
            curState[`transit_${i}`] = curState[`transit_${i + 1}`] ?? 0;
          }
          for (const k of Object.keys(curState)) {
            if (k.startsWith('transit_')) {
              const idx = parseInt(k.replace('transit_', ''), 10);
              if (idx >= delayTicks - 1 && k !== `transit_${delayTicks - 1}`) {
                drainable += curState[k] ?? 0;
                delete curState[k];
              }
            }
          }
          curState[`transit_${delayTicks - 1}`] = inflow;
          
          let totalTransit = 0;
          for (const k of Object.keys(curState)) {
             if (k.startsWith('transit_')) totalTransit += curState[k];
          }
          const unaccounted = stock + inflow - totalTransit - drainable;
          if (unaccounted > 0.01) {
             drainable += unaccounted;
          }
          
          outflow = applyGate(node, drainable, inflow, p, scope);
          curState['transit_0'] = (curState['transit_0'] ?? 0) + (drainable - outflow);
          // One chartable aggregate over the per-slot transit_N pipeline
          // (state:inTransit). After the held-back remainder returns to
          // transit_0 this equals stock + inflow − outflow — the new stock.
          curState.inTransit = totalTransit + (drainable - outflow);
        }
        nodeState[id] = curState;
        break;
      }
      case 'assembler': {
        const recipe = node.assembler?.recipe ?? {};
        const curState = { ...(nodeState[id] ?? {}) };
        
        // Accrue physical inflow by ingredient key
        const physicalInEdges = (inEdges.get(id) ?? []).filter(isPhysicalIn);
        for (const e of physicalInEdges) {
          const eFlow = edgeFlow[e.id] ?? 0;
          if (eFlow > 0) {
            const key = e.flowKind === 'custom' ? (e.customKind || 'custom') : e.flowKind;
            const stateKey = `inv_${key}`;
            curState[stateKey] = (curState[stateKey] ?? 0) + eFlow;
          }
        }
        
        // Calculate how many times we can apply the recipe
        let maxProduction = Number.POSITIVE_INFINITY;
        const recipeKeys = Object.keys(recipe);
        if (recipeKeys.length === 0) {
          maxProduction = 0;
        } else {
          for (const k of recipeKeys) {
            const required = recipe[k];
            if (required <= 0) continue;
            const availableStock = curState[`inv_${k}`] ?? 0;
            maxProduction = Math.min(maxProduction, availableStock / required);
          }
        }
        
        // Production, not the identity-derived stock, is the assembler's
        // meaningful quantity: the gate authorises up to maxProduction and the
        // recipe's inputs are consumed from the inv_<kind> accumulators.
        // Conservation defaults OFF for assemblers (units mix across the
        // recipe) — see sim.ts / backend/models/conservation.py.
        outflow = applyGate(node, maxProduction, maxProduction, p, scope);
        
        // Consume ingredients
        if (outflow > 0) {
          for (const k of recipeKeys) {
            const required = recipe[k];
            if (required <= 0) continue;
            const stateKey = `inv_${k}`;
            curState[stateKey] = Math.max(0, (curState[stateKey] ?? 0) - (outflow * required));
          }
        }
        nodeState[id] = curState;
        break;
      }
      case 'source':
      case 'actor':
      default:
        outflow = applyGate(node, available, inflow, p, scope);
        break;
    }
    nodeOutflow[id] = outflow;

    // ─── Evaluate Computed Metrics ─────────────────────────────────────────
    if (node.computedMetrics && node.computedMetrics.length > 0) {
      const curState = nodeState[id] ?? {};
      for (const m of node.computedMetrics) {
        if (!m.expr) continue;
        try {
          const val = evalExpr(m.expr, scope);
          curState[m.key] = val;
        } catch (e) {
          // ignore expression errors during tick
        }
      }
      nodeState[id] = curState;
    }

    // ─── Fan out across physical out-edges ─────────────────────────────────
    // NOTE: unlike the inflow filters, this keeps `conserves: false` edges —
    // they carry display flow (animated) even though the target ignores it.
    const physicalOut = (outEdges.get(id) ?? []).filter((e) => PHYSICAL_KINDS.has(e.flowKind));
    fanOut(node, outflow, physicalOut, edgeFlow, stocks, params);
  }

  // ─── Money pass ───────────────────────────────────────────────────────────
  const moneyIn = new Map<NodeId, number>();
  const moneyOut = new Map<NodeId, number>();
  for (const e of graph.edges) {
    if (e.flowKind !== 'money') continue;
    const conns = effectiveConnections(e);
    let total = 0;
    if (conns.length > 0) {
      for (const c of conns) {
        const sourceVal = resolveConnectionSource(c, e, edgeFlow, params);
        const transformed = applyConnectionExpr(c, sourceVal, params.edges[e.id] ?? {}, nodeById.get(e.source), nodeById.get(e.target), params);
        total += transformed;
      }
    }
    edgeFlow[e.id] = round(total);
    moneyIn.set(e.target, (moneyIn.get(e.target) ?? 0) + total);
    moneyOut.set(e.source, (moneyOut.get(e.source) ?? 0) + total);
  }

  // ─── Assemble TickPayload ─────────────────────────────────────────────────
  const nodeValues: TickPayload['nodeValues'] = {};
  for (const n of graph.nodes) {
    const isMoney = n.group === 'money';
    const inflow = isMoney
      ? moneyIn.get(n.id) ?? 0
      : n.kind === 'sink'
        ? 0 // sink's headline inflow is masked (its consumed is in state)
        : grossInflow(n.id, n, inEdges, edgeFlow, params, step, stocks);
    const outflow = isMoney ? moneyOut.get(n.id) ?? 0 : nodeOutflow[n.id] ?? 0;
    const prevStock = stocks[n.id] ?? 0;
    const stockNext = prevStock + inflow - outflow;
    const state = nodeState[n.id] && Object.keys(nodeState[n.id]).length > 0 ? nodeState[n.id] : undefined;
    nodeValues[n.id] = {
      inflow: round(inflow),
      outflow: round(outflow),
      stock: round(stockNext),
      state,
    };
  }

  return { step, nodeValues, edgeFlows: edgeFlow, params: flattenParams(params) };
}

/** Flatten resolved params into the setParam-path grammar the wire format uses
 *  for `tick.params` (`<nodeId>.<key>` / `e:<edgeId>.<key>`). Lets the chart
 *  builder plot knob changes over time. */
export function flattenParams(params: ResolvedParams): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [nid, kv] of Object.entries(params.nodes)) {
    for (const [k, v] of Object.entries(kv)) out[`${nid}.${k}`] = v;
  }
  for (const [eid, kv] of Object.entries(params.edges)) {
    for (const [k, v] of Object.entries(kv)) out[`e:${eid}.${k}`] = v;
  }
  return out;
}

// ─── Multi-graph (project) co-tick ─────────────────────────────────────────
// A project is a collection of named graphs; the sim co-ticks every graph each
// step into ONE merged `TickPayload`. Node/edge ids are globally unique across
// the project (enforced by `assertProjectUniqueIds`), so the merge is a plain
// union — no namespacing, no graph-qualified refs. Conservation is per-graph
// (asserted inside `computeTick`); the merge is additive.

/** Per-graph sim state threaded through `computeProjectTick`. Keyed by graph id
 *  in the `stateByGraph` map. */
export interface PerGraphSimState {
  params: ResolvedParams;
  stocks: Record<NodeId, number>;
  prevState?: Record<NodeId, Record<string, number>>;
}

/** Merge per-graph ticks into one flat `TickPayload`. Node/edge ids are
 *  globally unique, so a shallow union is correct. `step` is shared (one clock
 *  for the whole project). Annotations, when present, are unioned field by
 *  field. Pure. */
export function mergeTicks(ticks: TickPayload[]): TickPayload {
  if (ticks.length === 0) {
    return { step: 0, nodeValues: {}, edgeFlows: {} };
  }
  const step = ticks[0].step;
  const nodeValues: TickPayload['nodeValues'] = {};
  const edgeFlows: Record<EdgeId, number> = {};
  const params: Record<string, number> = {};
  const metricValues: Record<string, number> = {};
  // Annotation accumulators (built only if any tick carries annotations).
  let anyAnno = false;
  const bottlenecks: string[] = [];
  const anomalies: NonNullable<TickPayload['annotations']>['anomalies'] = [];
  const clusters: NonNullable<TickPayload['annotations']>['clusters'] = [];
  const decisions: NonNullable<TickPayload['annotations']>['decisions'] = [];
  for (const t of ticks) {
    Object.assign(nodeValues, t.nodeValues);
    Object.assign(edgeFlows, t.edgeFlows);
    if (t.params) Object.assign(params, t.params);
    if (t.metricValues) Object.assign(metricValues, t.metricValues);
    if (t.annotations) {
      anyAnno = true;
      bottlenecks.push(...(t.annotations.bottlenecks ?? []));
      anomalies.push(...(t.annotations.anomalies ?? []));
      clusters.push(...(t.annotations.clusters ?? []));
      decisions.push(...(t.annotations.decisions ?? []));
    }
  }
  const out: TickPayload = {
    step,
    nodeValues,
    edgeFlows,
    metricValues: Object.keys(metricValues).length ? metricValues : undefined,
  };
  if (Object.keys(params).length) out.params = params;
  if (anyAnno) {
    out.annotations = { bottlenecks, anomalies, clusters, decisions };
  }
  return out;
}

/** Co-tick every graph in a project: run `computeTick` per graph (with that
 *  graph's params/stocks/prevState), then merge. One step counter is shared
 *  across all graphs. The caller updates per-graph stocks/prevState from the
 *  returned per-graph ticks (see `computeProjectTickSplit` for the split form).
 *  Throws CycleError/ExprError from any graph. Pure. */
export function computeProjectTick(
  bundles: ScenarioBundle[],
  stateByGraph: Record<string, PerGraphSimState>,
  step: number,
): TickPayload {
  return mergeTicks(_computeProjectTicks(bundles, stateByGraph, step));
}

/** Like `computeProjectTick` but returns the per-graph ticks alongside the
 *  merged tick, so the caller can update per-graph stocks + prevState. Pure. */
export function computeProjectTickSplit(
  bundles: ScenarioBundle[],
  stateByGraph: Record<string, PerGraphSimState>,
  step: number,
): { merged: TickPayload; perGraph: TickPayload[] } {
  const perGraph = _computeProjectTicks(bundles, stateByGraph, step);
  return { merged: mergeTicks(perGraph), perGraph };
}

function _computeProjectTicks(
  bundles: ScenarioBundle[],
  stateByGraph: Record<string, PerGraphSimState>,
  step: number,
): TickPayload[] {
  const out: TickPayload[] = [];
  for (const b of bundles) {
    const s = stateByGraph[b.id];
    if (!s) continue; // a graph with no sim state doesn't tick (defensive)
    out.push(computeTick(b.graph, s.params, s.stocks, step, s.prevState));
  }
  return out;
}

/** Assert that node ids, edge ids, and metric keys are globally unique across
 *  the whole project (all graphs). This is what makes cross-graph references
 *  work without a graph qualifier. Throws on the first collision. Pure. */
export function assertProjectUniqueIds(bundles: ScenarioBundle[]): void {
  const nodes = new Set<string>();
  const edges = new Set<string>();
  const metrics = new Set<string>();
  for (const b of bundles) {
    for (const n of b.graph.nodes) {
      if (nodes.has(n.id)) throw new Error(`duplicate node id across project: ${n.id}`);
      nodes.add(n.id);
    }
    for (const e of b.graph.edges) {
      if (edges.has(e.id)) throw new Error(`duplicate edge id across project: ${e.id}`);
      edges.add(e.id);
    }
    for (const m of b.metrics) {
      if (metrics.has(m.key)) throw new Error(`duplicate metric key across project: ${m.key}`);
      metrics.add(m.key);
    }
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function grossInflow(
  id: NodeId,
  node: GraphNode,
  inEdges: Map<NodeId, GraphEdge[]>,
  edgeFlow: Record<EdgeId, number>,
  params: ResolvedParams,
  step: number,
  stocks: Record<NodeId, number>,
): number {
  const physicalIn = (inEdges.get(id) ?? []).filter(isPhysicalIn);
  let inflow = physicalIn.reduce((sum, e) => sum + (edgeFlow[e.id] ?? 0), 0);
  const isBoundarySource = node.kind === 'source' && node.conserves === false;
  const rate = sourceRate(node, params.nodes[id] ?? {}, step);
  if (isBoundarySource) inflow = rate;
  else if (node.kind === 'source' || node.source) inflow += rate;
  void stocks;
  return inflow;
}

function applyGate(
  node: GraphNode,
  available: number,
  inflow: number,
  p: Params,
  scope: Record<string, unknown>,
): number {
  if (node.kind !== 'gate' || !node.gate) {
    // Non-gate: pass-through.
    return Math.max(0, available);
  }
  const fn: GateFn = node.gate.fn;
  // The per-tick outflow cap lives in a param — which one is configurable via
  // gate.capacityParam (default 'capacity', mirroring source.rateParam /
  // delay.ticksParam). A missing param means uncapped.
  const capKey = node.gate.capacityParam ?? 'capacity';
  const capacity = p[capKey] ?? Number.POSITIVE_INFINITY;
  switch (fn) {
    case 'pass':
      return Math.max(0, Math.min(available, capacity === Infinity ? available : capacity));
    case 'threshold':
      return available <= 0 ? 0 : Math.min(available, capacity);
    case 'balance':
      return Math.max(0, Math.min(available, capacity));
    case 'argmax':
      // Routed at fan-out time; here just authorise up to capacity.
      return Math.max(0, Math.min(available, capacity));
    case 'formula': {
      const expr = node.gate.expr ?? (p.expr as unknown as string | undefined);
      if (!expr) return available;
      try {
        const v = evalExpr(expr, scope);
        return Number.isFinite(v) ? Math.max(0, Math.min(v, available)) : 0;
      } catch {
        return 0;
      }
    }
    default:
      return Math.max(0, available);
  }
  void inflow;
}

/** The edge's resolved weight: overlay value first (setParam / cell binding /
 *  agent delta all write there under the implicit 'weight' key), saved edge
 *  field as fallback. Bound weights may exceed 1 — the split normalises. */
function edgeWeight(e: GraphEdge, params: ResolvedParams): number {
  return params.edges[e.id]?.weight ?? e.weight;
}

function fanOut(
  node: GraphNode,
  outflow: number,
  physicalOut: GraphEdge[],
  edgeFlow: Record<EdgeId, number>,
  stocks: Record<NodeId, number>,
  params: ResolvedParams,
): void {
  if (physicalOut.length === 0) return;
  // Routing modes score out-edges by their targets' PREVIOUS-tick stocks —
  // the same one-tick lag signal edges use, so the choice is order-independent
  // and feedback loops resolve over time instead of cycling.
  const fn = node.gate?.fn;
  if (fn === 'argmax') {
    // Winner-takes-all: everything to the out-edge whose target holds the
    // least stock at the start of the tick. Ties break by higher weight,
    // then edge order.
    let best = physicalOut[0];
    let bestStock = Number.POSITIVE_INFINITY;
    let bestWeight = -1;
    for (const e of physicalOut) {
      const s = stocks[e.target] ?? 0;
      const w = edgeWeight(e, params);
      if (s < bestStock || (s === bestStock && w > bestWeight)) {
        best = e;
        bestStock = s;
        bestWeight = w;
      }
    }
    for (const e of physicalOut) edgeFlow[e.id] = e === best ? outflow : 0;
    return;
  }
  // Weighted split — for `balance`, each edge's weight is biased by its
  // target's need (emptier targets draw more); equal stocks degrade to the
  // plain weight split every other gate uses.
  const weights = physicalOut.map((e) => {
    const w = edgeWeight(e, params);
    return fn === 'balance' ? w / (1 + (stocks[e.target] ?? 0)) : w;
  });
  const totalW = weights.reduce((s, w) => s + w, 0) || 1;
  physicalOut.forEach((e, i) => {
    edgeFlow[e.id] = (outflow * weights[i]) / totalW;
  });
}

function sourceRate(node: GraphNode, p: Params, _step: number): number {
  if (node.kind !== 'source' && !node.source) return 0;
  const key = node.source?.rateParam ?? 'rate';
  const base = p[key] ?? 0;
  // Distribution noise: optional. constant → base. uniform/normal → base * (1 ± noise).
  // We honour `distParams` (`amplitude`) when distribution is set; otherwise base.
  const dist = node.source?.distribution;
  if (!dist || dist === 'constant') return base;
  const amp = p.noiseAmplitude ?? 0;
  if (amp === 0) return base;
  // Deterministic noise from a hashed step+nodeId so the same tick produces
  // the same value across sides (the Python mirror must match). We hash
  // _step into a unit-uniform value via a sine-based hash.
  const u = frac(Math.sin(_step * 12.9898 + hashStr(node.id) * 78.233) * 43758.5453);
  if (dist === 'uniform') return base + (u - 0.5) * 2 * amp;
  // normal ≈ average of three uniforms (cheap CLT approximation)
  const n = (frac(Math.sin((_step + 1) * 12.9898 + hashStr(node.id) * 78.233) * 43758.5453) +
    frac(Math.sin((_step + 2) * 12.9898 + hashStr(node.id) * 78.233) * 43758.5453) +
    u) / 3 - 0.5;
  return base + n * 2 * amp;
}

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h) / 0x7fffffff;
}
function frac(x: number): number {
  return x - Math.floor(x);
}

function readNodeState(
  nodeId: NodeId,
  key: string,
  stocks: Record<NodeId, number>,
  nodeState: Record<NodeId, Record<string, number>>,
): number {
  if (key === 'stock') return stocks[nodeId] ?? 0;
  if (key === 'inflow' || key === 'outflow') return 0; // not pre-computed for signal sources; expose via scope instead
  return nodeState[nodeId]?.[key] ?? stocks[nodeId] ?? 0;
}

/** Resolve a money edge's connection source value. */
function resolveConnectionSource(
  c: EdgeConnection,
  edge: GraphEdge,
  edgeFlow: Record<EdgeId, number>,
  params: ResolvedParams,
): number {
  if (c.kind === 'derive') {
    const srcEdge = c.sourceParam;
    return srcEdge ? edgeFlow[srcEdge] ?? 0 : 0;
  }
  if (c.kind === 'rate') {
    // Fixed rate. The legacy `derive.rateParam` referenced an EDGE param, so
    // the migrated `sourceParam` is an edge param key by default. Read it
    // from the edge's resolved params. (If a future author wants a source-
    // node param they write the expr as `source.<key>` and set sourceParam
    // to a no-op.)
    const key = c.sourceParam ?? 'rate';
    return params.edges[edge.id]?.[key] ?? params.nodes[edge.source]?.[key] ?? 0;
  }
  return 0;
}

/** Apply a connection's CEL expr to a source value. The expr scope is:
 *  `x` = source value, `source.<key>` = source node params (resolved),
 *  `target.<key>` = target node params, `params.<key>` = this edge's own params.
 *  Default expr 'x' returns the source value unchanged. */
function applyConnectionExpr(
  c: EdgeConnection | undefined,
  sourceVal: number,
  edgeParams: Params,
  sourceNode: GraphNode | undefined,
  targetNode: GraphNode | undefined,
  params: ResolvedParams,
): number {
  if (!c || !c.expr || c.expr === 'x') return sourceVal;
  const scope: Record<string, unknown> = {
    x: sourceVal,
    params: edgeParams,
  };
  if (sourceNode) {
    scope.source = params.nodes[sourceNode.id] ?? {};
  }
  if (targetNode) {
    scope.target = params.nodes[targetNode.id] ?? {};
  }
  try {
    const v = evalExpr(c.expr, scope);
    return Number.isFinite(v) ? v : 0;
  } catch {
    return 0;
  }
}

/** Build the formula scope for a node: self.*, nodes.X.*, edges.ID.*. */
function buildNodeScope(
  stock: number,
  inflow: number,
  p: Params,
  graph: Graph,
  params: ResolvedParams,
  edgeFlow: Record<EdgeId, number>,
  stocks: Record<NodeId, number>,
  nodeState: Record<NodeId, Record<string, number>>,
): Record<string, unknown> {
  const self: Record<string, number> = { stock, inflow, outflow: 0, ...p };
  const nodesScope: Record<string, unknown> = {};
  for (const n of graph.nodes) {
    const np: Record<string, number> = { stock: stocks[n.id] ?? 0, ...(params.nodes[n.id] ?? {}) };
    const st = nodeState[n.id];
    if (st) for (const [k, v] of Object.entries(st)) np[k] = v;
    nodesScope[n.id] = np;
  }
  const edgesScope: Record<string, unknown> = {};
  for (const e of graph.edges) {
    edgesScope[e.id] = { flow: edgeFlow[e.id] ?? 0, ...(params.edges[e.id] ?? {}) };
  }
  return { self, nodes: nodesScope, edges: edgesScope };
}

/** Topo sort. Treats edges passing `keep` as dependencies (target depends on
 *  source). Throws CycleError if the kept subgraph has a cycle.
 *
 *  Boundary exemption: an edge into a node whose `conserves === false` is NOT
 *  a dependency — boundary nodes (sources that also receive, sinks) derive
 *  their outflow from their rate, not from what they receive, so their
 *  incoming physical edges are consumed without gating evaluation. Without
 *  this exemption, a customer that emits orders AND receives meals forms a
 *  spurious cycle through the meal-delivery loop. */
function topoOrder(graph: Graph, keep: (e: GraphEdge) => boolean): NodeId[] {
  const nodeById = new Map(graph.nodes.map((n) => [n.id, n]));
  const inDeg = new Map<NodeId, number>();
  const adj = new Map<NodeId, NodeId[]>();
  for (const n of graph.nodes) {
    inDeg.set(n.id, 0);
    adj.set(n.id, []);
  }
  for (const e of graph.edges) {
    if (!keep(e)) continue;
    const target = nodeById.get(e.target);
    if (target && target.conserves === false) continue; // boundary: incoming flow is consumed, not a dep
    inDeg.set(e.target, (inDeg.get(e.target) ?? 0) + 1);
    adj.get(e.source)?.push(e.target);
  }
  const queue: NodeId[] = [];
  for (const [id, d] of inDeg) if (d === 0) queue.push(id);
  const out: NodeId[] = [];
  while (queue.length) {
    const id = queue.shift()!;
    out.push(id);
    for (const next of adj.get(id) ?? []) {
      const d = (inDeg.get(next) ?? 0) - 1;
      inDeg.set(next, d);
      if (d === 0) queue.push(next);
    }
  }
  if (out.length < graph.nodes.length) {
    const remaining = graph.nodes.filter((n) => !out.includes(n.id)).map((n) => n.id);
    const cycle = findCycle(graph, keep, remaining) ?? remaining;
    throw new CycleError(cycle);
  }
  return out;
}

function findCycle(
  graph: Graph,
  keep: (e: GraphEdge) => boolean,
  candidates: NodeId[],
): NodeId[] | null {
  const adj = new Map<NodeId, NodeId[]>();
  for (const n of graph.nodes) adj.set(n.id, []);
  for (const e of graph.edges) {
    if (keep(e)) adj.get(e.source)?.push(e.target);
  }
  const WHITE = 0;
  const GRAY = 1;
  const BLACK = 2;
  const color = new Map<NodeId, number>();
  for (const n of graph.nodes) color.set(n.id, WHITE);
  const parent = new Map<NodeId, NodeId | null>();
  let cycleStart: NodeId | null = null;
  let cycleEnd: NodeId | null = null;
  const dfs = (u: NodeId): boolean => {
    color.set(u, GRAY);
    for (const v of adj.get(u) ?? []) {
      if (color.get(v) === GRAY) {
        cycleStart = v;
        cycleEnd = u;
        return true;
      }
      if (color.get(v) === WHITE) {
        parent.set(v, u);
        if (dfs(v)) return true;
      }
    }
    color.set(u, BLACK);
    return false;
  };
  for (const start of candidates) {
    if (color.get(start) === WHITE) {
      if (dfs(start)) break;
    }
  }
  if (cycleStart === null || cycleEnd === null) return null;
  const cycle: NodeId[] = [cycleStart];
  let cur: NodeId | null = cycleEnd;
  while (cur !== null && cur !== cycleStart) {
    cycle.push(cur);
    cur = parent.get(cur) ?? null;
  }
  cycle.push(cycleStart);
  return cycle.reverse();
}

function round(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/** Recompute a node's params set when its ParamSpecs change (the editor). */
export function mergeParams(specs: ParamSpec[], existing: Params = {}): Params {
  const p: Params = {};
  for (const s of specs) p[s.key] = existing[s.key] ?? s.value;
  return p;
}

// Silence ExprError unused import if not referenced in some build configs.
void ExprError;
